if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      // A copy of BUBBLE_SECTIONS in frontend/cart/cart-bubbles.js. This file
      // is a classic script and cannot import it.
      static BUBBLE_SECTIONS = [
        "cart-icon-bubble",
        "cart-icon-bubble-product",
        "cart-icon-bubble-header",
      ];

      constructor() {
        super();

        this.form = this.querySelector("form");
        // Explicit selection (main PDP): keep an empty id input disabled so the
        // form can never submit a variant the shopper hasn't chosen.
        const explicitSelection = this.closest("[data-explicit-selection]");
        if (!explicitSelection || this.variantIdInput.value) {
          this.variantIdInput.disabled = false;
        }
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        // Where this element is allowed to write the button's label. Dawn wrote
        // it into the button's first <span>, which on the product page is now the
        // stack of state faces (decision 56): one write replaced all of them with
        // a text node and the CTA lost every state but the one being written.
        // `[data-submit-text]` is the product page's own slot for those writes;
        // every other product form keeps the old first-span behaviour.
        this.submitButtonText =
          this.submitButton.querySelector("[data-submit-text]") ||
          this.submitButton.querySelector("span");



        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        // Intercept add-to-cart when the requested quantity (plus what's already
        // in the cart for this variant) would exceed the variant's sellable cap.
        // Mirrors the cart's enforcement. On the main PDP the cap + cart quantity
        // live on <product-cta>; the legacy .quantity__input is the fallback for
        // any other surface that still renders a quantity selector.
        const cta = this.closest("product-cta");
        const qtyInput = this.closest("product-info")?.querySelector(
          ".quantity__input"
        );
        const cap = parseInt(
          cta?.dataset.maximumSellableQuantity ??
            qtyInput?.dataset.maximumSellableQuantity,
          10
        );
        if (Number.isFinite(cap)) {
          const cartQty =
            parseInt(
              cta?.dataset.cartQuantity ?? qtyInput?.dataset.cartQuantity,
              10
            ) || 0;
          const requested =
            parseInt(new FormData(this.form).get("quantity"), 10) || 1;
          if (cartQty + requested > cap) {
            if (!this.hideErrors) window.JdToast?.showQuantityLimit(cap);
            return;
          }
        }

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        // The cart badges (frontend/cart/cart-bubbles.js). On the product
        // page <product-cta> swaps them in when its orange dot lands.
        const sectionIds = [...this.constructor.BUBBLE_SECTIONS];
        // Explicit-selection PDP: also pull the lean product-variant-data section so
        // product-info.js can refresh the island's cart quantities after the add.
        if (this.closest("[data-explicit-selection]")) {
          sectionIds.push("product-variant-data");
        }
        formData.append("sections", sectionIds);
        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers"
            );
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: formData.get("id"),
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker(
                "add:wait-for-subscribers",
                startMarker
              );
            });

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.showError(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            }

            if (!this.error) this.error = false;
            // Outside the product page's pill nothing else swaps the badges in.
            if (!this.closest("product-cta")) this.patchBubbles(response.sections);
          })
          .catch((e) => {
            console.error(e);
            this.showError();
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // The same swap as patchBubbles() in frontend/cart/cart-bubbles.js.
      patchBubbles(sections) {
        if (!sections) return;
        const parser = new DOMParser();
        this.constructor.BUBBLE_SECTIONS.forEach((key) => {
          const target = document.getElementById(key);
          if (!sections[key] || !target) return;
          const inner = parser
            .parseFromString(sections[key], "text/html")
            .getElementById(key)?.innerHTML;
          if (inner != null) target.innerHTML = inner;
        });
      }

      // Every add error goes to the toast (frontend/utils/toast.js, Figma
      // 10362:7417), as on every add path: decision 84. The form's own error
      // line is gone. A gift card with the recipient form keeps its own
      // errors, as Dawn's `hideErrors` did before.
      showError(message) {
        if (this.hideErrors) return;
        window.JdToast?.show(message || window.cartStrings?.error);
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
